import { numberToDollars } from '../../../../utils'

// Builds the AdviceDataTable columns/rows for the SSI step from the SSI benefits
// model. The step is read-only: rows just reflect each person's current SSI
// configuration. `receivingSsi === 1` means "already receiving"; otherwise the
// person has a future start age (`paymentBeginAge`).

// Placeholder shown for the start age / monthly amount of an excluded person.
const DASH = '–'

/**
 * True when every person included in planning has SSI excluded. Drives both the
 * before-tax note (hidden) and the "don't show me again" checkbox (shown).
 */
export function allSsiExcluded({ spouseIncludedInPlanning, primary, spouse }) {
  if (!primary?.includeSsi) {
    return !spouseIncludedInPlanning || !spouse?.includeSsi
  }
  return false
}

/**
 * Column definitions. The name column's header is blank when the spouse is
 * excluded from planning: there's a single "Your SSI" row, so a "Name" heading
 * would be redundant (per the design).
 */
export function buildColumns({ spouseIncludedInPlanning }) {
  return [
    { key: 'name', label: spouseIncludedInPlanning ? 'Name' : '' },
    { key: 'startAge', label: 'Start age' },
    { key: 'monthlyAmount', label: 'Monthly amt' },
  ]
}

// Start-age cell: "Started" once receiving, the numeric age before then, a dash
// when excluded.
function startAgeValue(benefits) {
  if (!benefits.includeSsi) return DASH
  return benefits.receivingSsi === 1 ? 'Started' : `${benefits.paymentBeginAge}`
}

// Monthly-amount cell: the estimated/entered amount, a dash when excluded.
function monthlyAmountValue(benefits) {
  if (!benefits.includeSsi) return DASH
  return `$${numberToDollars(benefits.monthlyAmount, true)}`
}

function toRow({ id, desktopName, cardTitle, benefits }) {
  return {
    id,
    included: !!benefits.includeSsi,
    cardTitle,
    values: {
      name: desktopName,
      startAge: startAgeValue(benefits),
      monthlyAmount: monthlyAmountValue(benefits),
    },
  }
}

/**
 * Rows in display order: participant first, then spouse (only when the spouse is
 * included in planning).
 *
 * The participant's desktop name is their preferred name, or "Your SSI" when the
 * spouse is excluded from planning. Mobile card titles always use the
 * "Your SSI" / "<name>'s SSI" form.
 */
export function buildRows({ spouseIncludedInPlanning, personName, spouseName, primary, spouse }) {
  const rows = [
    toRow({
      id: 'primary',
      desktopName: spouseIncludedInPlanning ? personName : 'Your SSI',
      cardTitle: 'Your SSI',
      benefits: primary,
    }),
  ]

  if (spouseIncludedInPlanning && spouse) {
    rows.push(
      toRow({
        id: 'spouse',
        desktopName: spouseName,
        cardTitle: `${spouseName}'s SSI`,
        benefits: spouse,
      })
    )
  }

  return rows
}
